import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const [kind, setKind] = useState("subscription");
  const [minutes, setMinutes] = useState(0);
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.kind) setKind(data.kind);
        if (data.minutes) setMinutes(data.minutes);
        if (data.payment_status === "paid") { await refreshUser(); setStatus("paid"); return; }
        if (data.payment_status === "expired" || data.status === "failed") { setStatus("error"); return; }
      } catch { /* keep trying */ }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setStatus("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-30"
        style={{ background: "radial-gradient(circle, hsl(175 75% 50% / 0.5) 0%, transparent 62%)", filter: "blur(70px)" }} />
      <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
        className="relative glass rounded-3xl p-10 text-center max-w-md w-full" data-testid="payment-success-card">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-primary mx-auto mb-5 animate-spin" />
            <h1 className="font-serif text-2xl mb-2">Confirming your payment…</h1>
            <p className="text-muted-foreground text-sm">Just a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-teal-500 mx-auto mb-5" />
            {kind === "topup" ? (
              <>
                <h1 className="font-serif text-3xl tracking-tight mb-2">{minutes} minutes added.</h1>
                <p className="text-muted-foreground text-sm mb-7">Your top-up is in and never expires. Pick up right where you left off.</p>
                <Button className="rounded-full w-full" onClick={() => navigate("/talk")} data-testid="payment-success-continue">Back to Xcelio</Button>
              </>
            ) : (
              <>
                <h1 className="font-serif text-3xl tracking-tight mb-2">Welcome to Premium.</h1>
                <p className="text-muted-foreground text-sm mb-7">Xcelio's real voice, the live avatar and unlimited practice are now yours.</p>
                <Button className="rounded-full w-full" onClick={() => navigate("/app")} data-testid="payment-success-continue">Enter Xcelio</Button>
              </>
            )}
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-primary mx-auto mb-5 animate-spin" />
            <h1 className="font-serif text-2xl mb-2">Almost there…</h1>
            <p className="text-muted-foreground text-sm mb-7">Your payment is processing. Premium unlocks automatically once it clears.</p>
            <Button variant="secondary" className="rounded-full w-full" onClick={() => navigate("/app")} data-testid="payment-pending-continue">Back to Xcelio</Button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-14 h-14 text-coral-500 mx-auto mb-5" />
            <h1 className="font-serif text-2xl mb-2">Something went wrong.</h1>
            <p className="text-muted-foreground text-sm mb-7">We couldn't confirm your payment. You haven't been charged if it didn't complete.</p>
            <Button className="rounded-full w-full" onClick={() => navigate("/upgrade")} data-testid="payment-error-retry">Try again</Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
